import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { StudentSignUpForm } from "../auth-widget/student-auth-widget/forms/StudentSignUpForm";
import DeeplinkRegisterHeader from "./DeeplinkRegisterHeader";
import { studentSignUpSchema } from "../../constants/resolvers";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CustomSelector } from "../components/CustomSelector";
import { useStudentSignUpDeeplinkMutation, useValidateDeeplinkEventMutation } from "../../store/auth/auth.api";
import { useGetSchoolDetailsMutation } from "../../store/ApiMutaion";
import { ROUTES } from "../../constants/routes";
import loaderGif from "../../assets/gif/newLoader.gif";
import { setPostSignupOverlay } from "../../utils/postSignupOverlay.utils";
import useLeadCapture from "../UseLeadCapture";
import { DEFAULT_COUNTRY } from "../../constants/country-config";

const DeeplinkRegisterManager = ({ toggleTAndC, togglePAndP, deeplinkParams }) => {
  const history = useHistory();
  const [isValidating, setIsValidating] = useState(true);
  const [redirectMessage, setRedirectMessage] = useState(null);
  const [isRegistrationSuccess, setIsRegistrationSuccess] = useState(false);
  const [schoolDetails, setSchoolDetails] = useState(null);
  const [validateDeeplinkEvent] = useValidateDeeplinkEventMutation();
  const [getSchoolDetails] = useGetSchoolDetailsMutation();
  const { handleLeadCapture } = useLeadCapture();

  const targetExamOptions = [
    { id: "JEE Mains", value: "JEE" },
    { id: "NEET", value: "NEET" }
  ];

  /**
   * Validate deeplink params on mount:
   * 1. Check school_id is present (required)
   * 2. If event params present, validate event exists
   * 3. Fetch school details for display
   * Note: School ID validation happens during registration - if invalid, error callback will redirect
   */
  useEffect(() => {
    const validateDeeplinkParams = async () => {
      if (!deeplinkParams?.school_id) {
        console.warn("Missing school_id in deeplink params");
        setRedirectMessage("Invalid registration link. Redirecting to registration page...");
        setTimeout(() => {
          history.replace(ROUTES.STUDENT_CREATE_ACCOUNT);
        }, 2000);
        return;
      }

      try {
        // Step 1: If event params are present, validate the event
        const hasEventParams = deeplinkParams.event && deeplinkParams.level && deeplinkParams.round;
        if (hasEventParams) {
          const eventValidation = await validateDeeplinkEvent({
            school_id: deeplinkParams.school_id,
            event_name: deeplinkParams.event,
            level: deeplinkParams.level,
            round: parseInt(deeplinkParams.round)
          });

          if (!eventValidation?.data?.event_exists) {
            console.warn("Invalid event in deeplink:", deeplinkParams);
            setRedirectMessage("Invalid event link. Redirecting to registration page...");
            setTimeout(() => {
              history.replace(ROUTES.STUDENT_CREATE_ACCOUNT);
            }, 2000);
            return;
          }
        }

        // Step 2: Fetch school details for display (optional - if fails, continue anyway)
        try {
          const schoolDetailsResponse = await getSchoolDetails(deeplinkParams.school_id);
          if (schoolDetailsResponse?.data?.data) {
            const { school_name, city } = schoolDetailsResponse.data.data;
            setSchoolDetails({ school_name, city });
          }
        } catch (error) {
          // School details fetch failure is not critical - continue with registration
          console.warn("Failed to fetch school details:", error);
        }

        setIsValidating(false);
      } catch (error) {
        console.error("Deeplink validation error:", error);
        setRedirectMessage("Invalid registration link. Redirecting to registration page...");
        setTimeout(() => {
          history.replace(ROUTES.STUDENT_CREATE_ACCOUNT);
        }, 2000);
      }
    };

    validateDeeplinkParams();
  }, [deeplinkParams, history, validateDeeplinkEvent, getSchoolDetails]);

  const deeplinkSignUpResolver = useMemo(() => {
    return yupResolver(
      studentSignUpSchema.shape({
        targetExam: yup.string().when("gradeCode", {
          is: (val) => val > 8,
          then: (schema) => schema.required("Please select a target exam (JEE/NEET)"),
          otherwise: (schema) => schema.notRequired()
        })
      })
    );
  }, []);

  const renderExtraFields = ({ register, errors, watch, setValue, handleInputKeyPress }) => {
    const gradeCode = watch("gradeCode");

    if (gradeCode > 8) {
      return (
        <div className="col-12 mb-12">
          <label className="tab-lable">Select Target Exam</label>
          <CustomSelector
            handleInputKeyPress={(field) => {
              if (field.name === "targetExam") {
                setValue("targetExam", field.id);
                // Trigger manual field check for the form button
                handleInputKeyPress({ name: "targetExam", value: field.id });
              }
            }}
            data={targetExamOptions}
            placeHolder={"Choose Target Exam"}
            defaultSelectedID={0}
            errors={errors}
            dispatchValue={"targetExam"}
            register={register}
          />
        </div>
      );
    }
    return null;
  };

  const getIsExtraFieldsValid = (values) => {
    if (values.gradeCode > 8) {
      return !!values.targetExam;
    }
    return true;
  };

  const transformRegisterPayload = useCallback(
    (formData) => {
      const payload = {
        // Base form fields (these will be mapped by the mutation)
        ...formData,
        // Event-specific fields from deeplink params (if event-level deeplink)
        ...(deeplinkParams?.event && {
          event_name: deeplinkParams.event,
          level: deeplinkParams.level,
          round: deeplinkParams.round
        }),
        // Map targetExam to preferred_exam for the API
        ...(formData.targetExam && {
          preferred_exam: formData.targetExam
        })
        // Note: phone number and ISD code are handled by the StudentSignUpForm
        // They will be passed as isd_code and phone fields
      };

      return payload;
    },
    [deeplinkParams]
  );

  const [customRegisterMutation] = useStudentSignUpDeeplinkMutation();

  const handleRegistrationError = useCallback(
    (errorMessage) => {
      if (errorMessage?.includes("Invalid school_code") || errorMessage?.includes("Invalid school")) {
        setTimeout(() => {
          history.replace(ROUTES.STUDENT_CREATE_ACCOUNT);
        }, 3000);
      }
    },
    [history]
  );

  const getPostSignupMessage = (contextMessage) => {
    if (contextMessage === "User already Exist") {
      return "Welcome back! Please wait while we are preparing your Dashboard";
    }
    return "Registration Success! Please wait while we are preparing your Dashboard";
  };

  const handleRegistrationSuccess = useCallback(
    (response) => {
      const contextMessage = response?.details?.context;
      const postSignupMessage = getPostSignupMessage(contextMessage);

      setIsRegistrationSuccess(true);
      setRedirectMessage(postSignupMessage);
      setPostSignupOverlay({ message: postSignupMessage });
      handleLeadCapture(response?.details).catch((error) => {
        console.error("Error pushing LSQ event:", error);
      });
    },
    [handleLeadCapture]
  );

  if (isValidating || (redirectMessage && !isRegistrationSuccess)) {
    return (
      <div className="col-md-6 col-lg-5 position-relative">
        <div className="membership__card membership__card_mob mx-auto ms-md-auto">
          <div
            className="tab-content d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: "300px" }}
          >
            <img src={loaderGif} alt="Validating..." style={{ width: "240px", height: "240px" }} />
            <p className="text-center mt-3 text-white">{redirectMessage || "Validating registration link..."}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-md-6 col-lg-5 position-relative">
      <div className="membership__card membership__card_mob mx-auto ms-md-auto">
        <DeeplinkRegisterHeader deeplinkParams={deeplinkParams} schoolDetails={schoolDetails} />
        <div className="tab-content">
          <h3 className="tab-heading">Create Account </h3>
          <StudentSignUpForm
            toggleTAndC={toggleTAndC}
            togglePAndP={togglePAndP}
            defaultInvitationCode={deeplinkParams?.school_id}
            isInvitationCodeDisabled={!!deeplinkParams?.school_id}
            customResolver={deeplinkSignUpResolver}
            renderExtraFields={renderExtraFields}
            getIsExtraFieldsValid={getIsExtraFieldsValid}
            extraDefaultValues={{ targetExam: "" }}
            transformRegisterPayload={transformRegisterPayload}
            customRegisterMutation={customRegisterMutation}
            onRegistrationSuccess={handleRegistrationSuccess}
            onRegistrationError={handleRegistrationError}
          />
        </div>
      </div>
    </div>
  );
};

export default DeeplinkRegisterManager;
